import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { of, Subject, Subscription } from 'rxjs';

import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { UserManagementService } from 'app/core/services/user-management.service';
import {
  catchError,
  mergeMap,
} from 'rxjs/operators';

@Component({
  selector: 'add-permission-dialog',
  template: `<div>
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <form [formGroup]="form" fxLayout="column">
        <mat-form-field>
          <input matInput formControlName="name" placeholder="Name" />
        </mat-form-field>
        <mat-form-field>
          <input
            matInput
            formControlName="description"
            placeholder="Description"
          />
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button mat-dialog-close>Cancel</button>
      <button
        mat-raised-button
        color="primary"
        (click)="submit()"
        [disabled]="!form.valid"
      >
        Submit
      </button>
    </mat-dialog-actions>
  </div>`,
  styleUrls: [],
})
export class AddPermissionDialogComponent {
  form = this.formBuilder.group({
    name: this.formBuilder.control(this.data.permission?.name ?? '', [
      Validators.required,
    ]),
    description: this.formBuilder.control(
      this.data.permission?.description ?? '',
      [Validators.required],
    ),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { title: string; permission?: any },
    public dialogRef: MatDialogRef<AddPermissionDialogComponent>,
    private formBuilder: FormBuilder,
  ) {}

  submit() {
    if (this.form.valid) {
      this.dialogRef.close(this.form.value);
    }
  }
}

@Component({
  selector: 'delete-permission-dialog',
  template: `<div>
    <h2 mat-dialog-title>Confirmation</h2>
    <mat-dialog-content>
      Are you sure that you want to delete {{ data.permission.name }}?
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-raised-button color="primary" [mat-dialog-close]="true">
        Submit
      </button>
    </mat-dialog-actions>
  </div>`,
  styleUrls: [],
})
export class DeletePermissionDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { permission?: any },
  ) {}
}

@Component({
  selector: 'manage-permissions',
  templateUrl: './manage-permissions.component.html',
  styleUrls: ['./manage-permissions.component.scss'],
})
export class ManagePermissionsComponent implements OnInit, OnDestroy {
  private subs: Subscription[] = [];
  private dispatchGet: Subject<void> = new Subject<void>();
  private dispatchCreate: Subject<any> = new Subject<any>();
  private dispatchUpdate: Subject<[string, any]> = new Subject<[string, any]>();
  private dispatchDelete: Subject<string> = new Subject<string>();

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);

  displayedColumns: string[] = ['name', 'description', 'menu'];

  constructor(
    private userManagementService: UserManagementService,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
  ) {}

  handleError(error) {
    this.snackbar.open(error.error.message, 'Dismiss');
    return of(null);
  }

  ngOnInit(): void {
    this.subs.push(
      this.dispatchGet
        .asObservable()
        .pipe(
          mergeMap(() =>
            this.userManagementService
              .getAllPermissions()
              .pipe(catchError(error => this.handleError(error))),
          ),
        )
        .subscribe(value => {
          this.dataSource.data = value['permissions'] as any[];
        }),
      this.dispatchCreate
        .asObservable()
        .pipe(
          mergeMap(value => {
            return this.userManagementService
              .createPermission(value)
              .pipe(catchError(error => this.handleError(error)));
          }),
        )
        .subscribe(() => {
          this.dispatchGet.next();
        }),
      this.dispatchUpdate
        .asObservable()
        .pipe(
          mergeMap(([id, value]) =>
            this.userManagementService
              .updatePermission(id, value)
              .pipe(catchError(error => this.handleError(error))),
          ),
        )
        .subscribe(() => {
          this.dispatchGet.next();
        }),
      this.dispatchDelete
        .asObservable()
        .pipe(
          mergeMap(value =>
            this.userManagementService
              .deletePermission(value)
              .pipe(catchError(error => this.handleError(error))),
          ),
        )
        .subscribe(() => {
          this.dispatchGet.next();
        }),
    );

    this.dispatchGet.next();
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  addPermission() {
    this.dialog
      .open(AddPermissionDialogComponent, {
        width: '400px',
        data: { title: 'Add Permission' },
      })
      .beforeClosed()
      .subscribe(value => {
        if (value) {
          this.dispatchCreate.next(value);
        }
      });
  }

  editPermission(permission: any) {
    this.dialog
      .open(AddPermissionDialogComponent, {
        width: '400px',
        data: { title: 'Edit Permission', permission },
      })
      .beforeClosed()
      .subscribe(value => {
        if (value) {
          this.dispatchUpdate.next([permission.uuid, value]);
        }
      });
  }

  deletePermission(permission: any) {
    this.dialog
      .open(DeletePermissionDialogComponent, {
        width: '400px',
        data: {
          permission,
        },
      })
      .afterClosed()
      .subscribe(value => {
        if (value) {
          this.dispatchDelete.next(permission.uuid);
        }
      });
  }
}
